import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { datadogRum } from '@datadog/browser-rum';
import { Modal } from '../Modal/Modal';
import HomepageAdsAPI, { LiveHomepageAd } from '../../api/HomepageAdsAPI';
import { sanitizeHomepageAdHtml } from '../../utils/homepageAdHtmlSanitize';
import { useCrispChat } from '../Crisp/CrispProvider';

interface HomeAdModalProps {
  ad: LiveHomepageAd | null;
  onClose: () => void;
}

const HomeAdModal: React.FC<HomeAdModalProps> = ({ ad, onClose }) => {
  const { openChat } = useCrispChat();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!ad) {
      return;
    }
    datadogRum.addAction('Homepage Ad - Opened', { adId: ad.id, campaignName: ad.name });
    void HomepageAdsAPI.recordEngagement(ad.id).catch((error) => {
      console.error('Failed to record homepage ad engagement', { adId: ad.id, error });
    });
  }, [ad]);

  if (!ad) {
    return null;
  }

  const handleOpenChat = async () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await HomepageAdsAPI.registerInterest(ad.id);
      datadogRum.addAction('Homepage Ad - Chat Clicked', {
        adId: ad.id,
        campaignName: ad.name,
        alreadyInterested: response.alreadyInterested,
      });
    } catch (error) {
      console.error('Failed to register homepage ad interest', { adId: ad.id, error });
    }
    onClose();
    openChat();
  };

  return (
    <Modal modalTitle={ad.modalTitle} canClose onClose={onClose}>
      <div className="space-y-6">
        <div
          className="prose max-w-none text-foreground dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: sanitizeHomepageAdHtml(ad.modalBody) }}
        />
        <p className="text-center text-sm text-muted-foreground">
          <Trans
            i18nKey="home.ad.chat_hint"
            defaults="Questions? Reach out by <chat>chat</chat>."
            components={{
              chat: (
                <button
                  type="button"
                  onClick={() => void handleOpenChat()}
                  disabled={isSubmitting}
                  className="text-foreground underline underline-offset-2 hover:text-muted-foreground"
                />
              ),
            }}
          />
        </p>
      </div>
    </Modal>
  );
};

export default HomeAdModal;
